import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import { useAuth } from '../../hooks/useAuth.js';
import { fetchFeatures } from '../../utils/features.js';
import { GOAL_STATUS, measurePct } from '../../views/GoalsView.jsx';

// Agenda item "Voortgang doelen" (Visie & doelen). Which agenda items of a meeting are
// one, and whether this user may add one — nothing is fetched while the feature is hidden.
export function useGoalAgenda(meetingId) {
  const auth = useAuth();
  const [info, setInfo] = useState({ item_ids: [], can_add: false });
  const reload = () => fetchFeatures(`${auth.userId}:${auth.activeClubId}`)
    .then(f => (f.goals ? api(`/api/goals/meetings/${meetingId}`) : null))
    .then(i => setInfo(i || { item_ids: [], can_add: false }))
    .catch(() => setInfo({ item_ids: [], can_add: false }));
  useEffect(() => { reload(); }, [meetingId, auth.userId, auth.activeClubId]);
  return [info, reload];
}

const fmtNum = v => (v === null || v === undefined ? '–' : Number(v).toLocaleString('nl-NL'));

// The goals of the body: live until the minutes are approved, then as recorded
export function GoalAgendaBlock({ meetingId, itemId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api(`/api/goals/meetings/${meetingId}/agenda/${itemId}`).then(setData).catch(() => setData({ goals: [], error: true }));
  }, [meetingId, itemId]);
  if (!data) return <div style="font-size:12px;color:var(--color-text-muted);margin-top:8px">Doelen laden…</div>;
  return (
    <div style="margin-top:8px;display:flex;flex-direction:column;gap:6px;min-width:0">
      <div style="font-size:12px;color:var(--color-text-muted)">
        {data.frozen_at ? 'Stand zoals vastgelegd bij het vaststellen van de notulen' : 'Actuele stand (wordt vastgelegd bij het vaststellen van de notulen)'}
      </div>
      {data.goals.length === 0 && <div style="font-size:13px;color:var(--color-text-muted)">Geen lopende doelen voor dit gremium.</div>}
      {data.goals.map(g => {
        const st = GOAL_STATUS[g.status] || GOAL_STATUS.op_schema;
        const m = measurePct(g);
        return (
          <div key={g.id} style={`padding:8px 10px;border:1px solid var(--color-border);border-left:4px solid ${st.color};border-radius:8px;font-size:13px;min-width:0`}>
            <div style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start;flex-wrap:wrap">
              <div style="min-width:0;flex:1 1 200px">
                <div style="font-weight:600">{g.title}</div>
                <div style="font-size:12px;color:var(--color-text-muted)">{[g.focus_area, g.owner].filter(Boolean).join(' · ')}</div>
              </div>
              <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${st.bg};color:${st.color};white-space:nowrap`}>{st.label}</span>
            </div>
            <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:4px;font-size:12px">
              {g.measure_label && <span>{g.measure_label}: <strong>{fmtNum(g.current_value)}</strong> van {fmtNum(g.baseline_value)} naar {fmtNum(g.target_value)}{m !== null ? ` (${m}%)` : ''}</span>}
              {g.progress?.total > 0 && <span>Werk: {g.progress.done} van {g.progress.total} af</span>}
            </div>
            {g.status_note && <div style="font-size:12px;font-style:italic;color:var(--color-text-muted);margin-top:2px">“{g.status_note}”</div>}
          </div>
        );
      })}
    </div>
  );
}
